import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Viewer, Worker, SpecialZoomLevel, ScrollMode } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { FieldModel } from "./types";
import { createFieldsPlugin } from "./createFieldsPlugin";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";

const PDFJS_VERSION = "2.16.105";

const useStyles = makeStyles((theme) => ({
	documentEditorContainer: {
		display: "flex",
		flexDirection: "column",
		gap: 0,
		flex: 1,
		minHeight: 0,
		height: "100%",
		position: "relative",
		backgroundColor: "#D8DEE6",
		[theme.breakpoints.down("md")]: {
			flex: "0 0 auto",
			height: "auto",
			minHeight: 0,
		},
	},
	viewerContainer: {
		flex: 1,
		position: "relative",
		overflow: "hidden",
		minHeight: 0,
		height: "100%",
		backgroundColor: "#D8DEE6",
		[theme.breakpoints.down("md")]: {
			flex: "0 0 auto",
			height: "auto",
			minHeight: 180,
			overflow: "visible",
		},
		// Keep PDF UI inside the viewport; scroll happens inside the viewer only.
		"& .rpv-default-layout__container": {
			height: "100%",
			maxHeight: "100%",
			minHeight: 0,
			display: "flex",
			flexDirection: "column",
			overflow: "hidden",
			[theme.breakpoints.down("md")]: {
				height: "auto",
				maxHeight: "none",
				overflow: "visible",
			},
		},
		"& .rpv-default-layout__main": {
			flex: 1,
			minHeight: 0,
			minWidth: 0,
			display: "flex",
			flexDirection: "row",
			overflow: "hidden",
			[theme.breakpoints.down("md")]: {
				flexDirection: "column",
				flex: "0 0 auto",
				height: "auto",
				overflow: "visible",
			},
		},
		"& .rpv-default-layout__body": {
			flex: 1,
			minHeight: 0,
			minWidth: 0,
			display: "flex",
			flexDirection: "column",
			overflow: "hidden",
			backgroundColor: "#D8DEE6",
			[theme.breakpoints.down("md")]: {
				flex: "0 0 auto",
				height: "auto",
				overflow: "visible",
			},
		},
		"& .rpv-default-layout__body > div:last-child": {
			flex: 1,
			minHeight: 0,
			overflow: "hidden",
			display: "flex",
			flexDirection: "column",
			[theme.breakpoints.down("md")]: {
				flex: "0 0 auto",
				height: "auto",
				overflow: "visible",
			},
		},
		"& .rpv-default-layout__toolbar": {
			flexShrink: 0,
			backgroundColor: "#fff",
			borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
			[theme.breakpoints.down("md")]: {
				flexWrap: "wrap",
				justifyContent: "center",
				rowGap: 4,
				paddingTop: theme.spacing(0.5),
				paddingBottom: theme.spacing(0.5),
				overflowX: "auto",
				maxWidth: "100%",
			},
		},
		// Stronger “paper” shadow + centered page focus (react-pdf-viewer CSS variables)
		"& .rpv-core__viewer": {
			flex: 1,
			minHeight: 0,
			height: "100%",
			overflow: "auto",
			...({
				"--rpv-core__page-layer-box-shadow":
					"0 14px 44px rgba(0, 38, 86, 0.18), 0 4px 14px rgba(0, 0, 0, 0.12)",
			} as Record<string, string>),
			[theme.breakpoints.down("md")]: {
				flex: "0 0 auto",
				height: "auto",
				minHeight: 160,
				overflow: "visible",
				WebkitOverflowScrolling: "touch",
			},
		},
		"& .rpv-core__inner-pages": {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		},
		"& .rpv-core__inner-page": {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
		},
		"& .rpv-core__page-layer": {
			overflow: "visible",
		},
	},
	/** Submitted/review: allow wheel scroll through all pages (default layout uses overflow:hidden) */
	viewerContainerReview: {
		overflow: "auto",
		WebkitOverflowScrolling: "touch",
		"& .rpv-default-layout__container": {
			height: "100%",
			maxHeight: "100%",
			overflow: "hidden",
			display: "flex",
			flexDirection: "column",
		},
		"& .rpv-default-layout__main": {
			flex: 1,
			minHeight: 0,
			overflow: "hidden",
		},
		"& .rpv-default-layout__body": {
			flex: 1,
			minHeight: 0,
			overflow: "hidden",
		},
		"& .rpv-default-layout__body > div:last-child": {
			flex: 1,
			minHeight: 0,
			overflow: "auto",
			display: "flex",
			flexDirection: "column",
		},
		"& .rpv-core__viewer": {
			flex: 1,
			minHeight: 0,
			height: "auto",
			maxHeight: "none",
			overflow: "auto",
			WebkitOverflowScrolling: "touch",
		},
		"& .rpv-core__inner-container": {
			overflow: "visible",
		},
		"& .rpv-core__inner-pages": {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		},
	},
	buttonContainer: {
		display: "flex",
		justifyContent: "center",
		gap: 16,
		padding: "16px",
		backgroundColor: "white",
	},
	rejectButton: {
		backgroundColor: theme.palette.error.main,
		color: "white",
		"&:hover": {
			backgroundColor: theme.palette.error.dark,
		},
	},
	completeButton: {
		backgroundColor: theme.palette.success.main,
		color: "white",
		"&:hover": {
			backgroundColor: theme.palette.success.dark,
		},
	},
	fieldsContainer: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		pointerEvents: "none",
		"& > *": {
			pointerEvents: "auto",
		},
	},
}));

interface FieldValue {
	id: string;
	value: string;
	fieldType: string;
}

interface DocumentEditorProps {
	fileUrl: string;
	fields: FieldModel[];
	fieldValues?: FieldValue[];
	activeFieldId?: string | null;
	scrollToFieldRef?: React.MutableRefObject<((fieldId: string) => void) | null>;
	/** Submitted / read-only: minimal PDF overlays, vertical scroll */
	reviewMode?: boolean;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
	fileUrl,
	fields,
	fieldValues = [],
	activeFieldId,
	scrollToFieldRef,
	reviewMode = false,
}) => {
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const viewerContainerRef = useRef<HTMLDivElement>(null);
	const classes = useStyles();
	const muiTheme = useTheme();
	const isNarrowViewport = useMediaQuery(muiTheme.breakpoints.down("md"));

	// Initialize page navigation plugin
	const pageNavigationPluginInstance = pageNavigationPlugin();
	const { jumpToPage } = pageNavigationPluginInstance;

	const layout = defaultLayoutPlugin({
		sidebarTabs: (defaultTabs) => [],
		toolbarPlugin: {
			fullScreenPlugin: {
				onEnterFullScreen: (zoom) => {
					zoom(1.5);
					return Promise.resolve();
				},
			},
		},
	});

	const onSelect = useCallback((id: string) => {
		setSelectedId(id);
	}, []);

	// Scroll to field function
	const scrollToField = useCallback((fieldId: string) => {
		const field = fields.find(f => f.id === fieldId);
		if (!field || !field.fieldPosition) {
			console.warn(`Field ${fieldId} has no position data`);
			return;
		}

		const { page } = field.fieldPosition;

		// Navigate to the page (convert from 1-based to 0-based index)
		jumpToPage(page - 1);

		// Wait for page to render, then scroll to field
		setTimeout(() => {
			const fieldElement = document.querySelector(`[data-field-id="${fieldId}"]`);
			if (fieldElement) {
				fieldElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center'
				});
			}
		}, 300);
	}, [fields, jumpToPage]);

	// Expose scroll function via ref
	useEffect(() => {
		if (scrollToFieldRef) {
			scrollToFieldRef.current = scrollToField;
		}
		return () => {
			if (scrollToFieldRef) {
				scrollToFieldRef.current = null;
			}
		};
	}, [scrollToField, scrollToFieldRef]);

	const pluginInstance = useMemo(
		() =>
			createFieldsPlugin(
				fields,
				onSelect,
				selectedId,
				fieldValues,
				reviewMode ? null : activeFieldId,
				reviewMode
			),
		[fields, onSelect, selectedId, fieldValues, activeFieldId, reviewMode]
	);


	const viewerClasses = reviewMode
		? `${classes.viewerContainer} ${classes.viewerContainerReview}`
		: classes.viewerContainer;

	return (
		<div className={classes.documentEditorContainer}>
			<div
				className={viewerClasses}
				ref={viewerContainerRef}
				onClick={() => setSelectedId(null)}
			>
				<Worker
					workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/legacy/build/pdf.worker.js`}
				>
					<Viewer
						key={`${fileUrl}-${isNarrowViewport ? "fitw" : "100"}-${reviewMode ? "review" : "edit"}`}
						defaultScale={
							isNarrowViewport ? SpecialZoomLevel.PageWidth : 1
						}
						scrollMode={ScrollMode.Vertical}
						fileUrl={fileUrl}
						plugins={[pageNavigationPluginInstance, layout, pluginInstance]}
					/>
				</Worker>
			</div>
		</div>
	);
};

export default DocumentEditor;
